import styled from "styled-components"

import { getCoverData, lerpColor } from "../../pdf/coverLogic"
import type { IntelligenceReportData } from "../../pdf/types"
import { PageCard } from "../shared"

const CoverContent = styled.div`
  position: relative;
  z-index: 1;
  height: 100%;
  padding: 24px 24px 16px;
  display: flex;
  flex-direction: column;
  color: #f8fafc;
`

const BgGradient = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
`

const LogoArea = styled.div`
  height: 50px;
  display: flex;
  align-items: center;
  margin-bottom: 12px;
`

const LogoImage = styled.img`
  height: 44px;
  object-fit: contain;
`

const LogoText = styled.div`
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1px;
`

const Greeting = styled.div`
  font-size: 16px;
  color: #fbfcfe;
  margin-bottom: 6px;
  line-height: 1.3;
`

const Heading = styled.h1`
  font-size: 48px;
  font-weight: 700;
  color: #f8fafc;
  line-height: 1.06;
  letter-spacing: -2px;
  margin: 0 0 8px;
`

const Description = styled.p`
  font-size: 16px;
  color: #f3f5ff;
  line-height: 1.3;
  margin: 0 0 10px;
`

const StatusCard = styled.div`
  background: rgba(20, 20, 40, 0.5);
  border-radius: 16px;
  display: flex;
  padding: 16px;
  gap: 16px;
  margin-bottom: 12px;
  align-items: center;
`

const GaugeContainer = styled.div`
  position: relative;
  width: 90px;
  height: 90px;
`

const GaugeOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const GaugeValue = styled.div`
  font-size: 30px;
  font-weight: 700;
  line-height: 35px;
  color: #f8fafc;
`

const GaugeLabel = styled.div`
  width: 71px;
  font-size: 7px;
  font-weight: 700;
  color: #d8dbf2;
  letter-spacing: 1.6px;
  text-align: center;
`

const StatusRight = styled.div`
  flex: 1;
`

const StatusLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #e5e7f2;
  letter-spacing: 2px;
  margin-bottom: 4px;
`

const StatusValue = styled.div`
  font-size: 39.5px;
  font-weight: 700;
  color: #f8fafc;
  line-height: 1.06;
  letter-spacing: -1px;
  margin-bottom: 6px;
  white-space: pre-line;
`

const StatusDescription = styled.div`
  font-size: 14px;
  color: #f1f1f6;
  opacity: 0.8;
  line-height: 1.5;
`

const StatsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 8px;
`

const StatCard = styled.div<{ $isLeft: boolean }>`
  width: 48.5%;
  background: rgba(20, 20, 40, 0.5);
  border-radius: 16px;
  border: 1px solid #3a4353;
  padding: 12px;
  margin-bottom: 6px;
  ${p => p.$isLeft && "margin-right: 3%;"}
`

const StatTopBorder = styled.div<{ $color: string }>`
  width: 50px;
  height: 4px;
  border-radius: 2px;
  background: ${p => p.$color};
  margin-bottom: 10px;
`

const StatLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #d8dbf2;
  letter-spacing: 1.6px;
  margin-bottom: 6px;
`

const StatValue = styled.div<{ $color: string }>`
  font-size: 24px;
  font-weight: 700;
  color: ${p => p.$color};
  margin-bottom: 6px;
`

const StatDescription = styled.div`
  font-size: 12.5px;
  color: #f6f6f6;
  line-height: 1.32;
`

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 8px;
`

const FooterText = styled.div`
  font-size: 12px;
  color: #ffffff;
  line-height: 1.333;
  text-align: center;
  opacity: 0.8;
`

// Gauge helpers (mirror pdf/CoverPage)
const toRad = (deg: number) => (deg * Math.PI) / 180

function polarXY(cx: number, cy: number, r: number, degFromTop: number) {
  const rad = toRad(degFromTop - 90)
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }
}

function arcPath(cx: number, cy: number, r: number, startDeg: number, endDeg: number): string {
  const s = polarXY(cx, cy, r, startDeg)
  const e = polarXY(cx, cy, r, endDeg)
  const large = endDeg - startDeg > 180 ? 1 : 0
  return `M ${s.x.toFixed(3)} ${s.y.toFixed(3)} A ${r} ${r} 0 ${large} 1 ${e.x.toFixed(3)} ${e.y.toFixed(3)}`
}

const GAUGE_SIZE = 90
const GAUGE_PROGRESS_STROKE = 7
const GAUGE_TRACK_STROKE = GAUGE_PROGRESS_STROKE + 4
const GAUGE_SEGMENTS = 48

function GaugeChart({ percentage, gradient }: { percentage: number; gradient: [string, string] }) {
  const r = (GAUGE_SIZE - GAUGE_TRACK_STROKE) / 2
  const c = GAUGE_SIZE / 2

  const pct = Math.min(Math.max(percentage, 0), 100)
  const sweepDeg = Math.min((pct / 100) * 360, 359.99)

  const progressSegments: Array<{ d: string; color: string; cap: "round" | "butt" }> = []
  if (pct > 0) {
    for (let i = 0; i < GAUGE_SEGMENTS; i++) {
      const t0 = i / GAUGE_SEGMENTS
      const t1 = (i + 1) / GAUGE_SEGMENTS
      const segStart = t0 * sweepDeg
      const segEnd = Math.min(t1 * sweepDeg + 0.75, sweepDeg)
      const mid = (t0 + t1) / 2
      const pingPong = mid <= 0.5 ? mid * 2 : (1 - mid) * 2
      progressSegments.push({
        d: arcPath(c, c, r, segStart, segEnd),
        color: lerpColor(gradient[0], gradient[1], pingPong),
        cap: i === 0 || i === GAUGE_SEGMENTS - 1 ? "round" : "butt",
      })
    }
  }

  return (
    <GaugeContainer>
      <svg width={GAUGE_SIZE} height={GAUGE_SIZE} viewBox={`0 0 ${GAUGE_SIZE} ${GAUGE_SIZE}`}>
        <circle cx={c} cy={c} r={r} stroke="#1E1E36" strokeOpacity={0.4} strokeWidth={GAUGE_TRACK_STROKE} fill="none" />
        {progressSegments.map((seg, idx) => (
          <path
            key={idx}
            d={seg.d}
            stroke={seg.color}
            strokeWidth={GAUGE_PROGRESS_STROKE}
            fill="none"
            strokeLinecap={seg.cap}
          />
        ))}
      </svg>
      <GaugeOverlay>
        <GaugeValue>{pct}</GaugeValue>
        <GaugeLabel>PRIORITY LEVEL</GaugeLabel>
      </GaugeOverlay>
    </GaugeContainer>
  )
}

interface Props {
  report: IntelligenceReportData
  logoUrl?: string | null
}

export function CoverPage({ report, logoUrl }: Props) {
  const { priorityPct, copy, tagline, greetingName, tiles } = getCoverData(report)

  return (
    <PageCard background="#051B2D">
      <BgGradient>
        <svg width="595" height="842" viewBox="0 0 595 842">
          <defs>
            <linearGradient id="coverGradHtml" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#051B2D" />
              <stop offset="1" stopColor="#7E7AF5" />
            </linearGradient>
            <radialGradient id="redGlowHtml" cx={0.5} cy={0.5} r={0.5} fx={0.5} fy={0.5}>
              <stop offset="0" stopColor="#EF4444" stopOpacity={0.2} />
              <stop offset="1" stopColor="#EF4444" stopOpacity={0} />
            </radialGradient>
            <radialGradient id="cyanGlowHtml" cx={0.5} cy={0.5} r={0.5} fx={0.5} fy={0.5}>
              <stop offset="0" stopColor="#4AE9FF" stopOpacity={0.3} />
              <stop offset="1" stopColor="#4AE9FF" stopOpacity={0} />
            </radialGradient>
          </defs>
          <rect x="0" y="0" width="595" height="842" fill="url(#coverGradHtml)" />
          <circle cx="521" cy="465" r="173" fill="url(#redGlowHtml)" />
          <circle cx="24" cy="762" r="200" fill="url(#cyanGlowHtml)" />
        </svg>
      </BgGradient>

      <CoverContent>
        <LogoArea>{logoUrl ? <LogoImage src={logoUrl} alt="logo" /> : <LogoText>COMPANY LOGO</LogoText>}</LogoArea>

        <Greeting>
          Hi {greetingName || "—"} — {copy.greetingSubtitle}
        </Greeting>

        <Heading>{copy.headline}</Heading>

        <Description>{tagline}</Description>

        <StatusCard>
          <GaugeChart percentage={priorityPct} gradient={copy.gradient} />
          <StatusRight>
            <StatusLabel>OVERALL STATUS</StatusLabel>
            <StatusValue>{copy.statusLabel}</StatusValue>
            <StatusDescription>{copy.statusDescription}</StatusDescription>
          </StatusRight>
        </StatusCard>

        <StatsGrid>
          {tiles.map((stat, idx) => (
            <StatCard key={idx} $isLeft={idx % 2 === 0}>
              <StatTopBorder $color={stat.color} />
              <StatLabel>{stat.label}</StatLabel>
              <StatValue $color={stat.color}>{stat.value}</StatValue>
              <StatDescription>{stat.description}</StatDescription>
            </StatCard>
          ))}
        </StatsGrid>

        <Footer>
          <FooterText>Powered by Nuve AI</FooterText>
          <FooterText>
            {report.start_date} – {report.end_date}
          </FooterText>
        </Footer>
      </CoverContent>
    </PageCard>
  )
}
